import React, { useState, useCallback, useEffect } from "react";
import ProgressTracker from "../../../../../utils/ProgressTracker";
import { Checkbox } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import {
  markHardwarePartComplete,
  markNotHardwarePartComplete,
} from "../../../../../store/slices/ProjectOngoing/projectOngoingSlice";
import { useSnackbar } from "notistack";
import { fetchWorkEntriesProjectTracking } from "../../../../../store/slices/workentriesSlice";
import LoadingSpinner from "../../../../../components/common/loader/LoadingSpinner";
import { fetchDescriptionTasksProjectTracking } from "../../../../../store/slices/DescriptionTask";
import { fetchHardwareProjectTracking } from "../../../../../store/slices/hardwareDescriptionSlice";
import {
  updateOngoingHardwareParts,
  updateOngoingDescription,
} from "../../../../../store/slices/ProjectOngoing/ProjectOngoingUpdateSlice";
import { useCurrencySymbol } from "../../../../../utils/currencyUtils";
import DateConversion from "../../../../../utils/DateConversion";
import CommaConvert from "../../../../../utils/CommaConvert";
import {
  formatAmountComma,
  formatAmountForInput,
  validateAmountValue,
  parseAmountNumber,
} from "../../../../../utils/decimalAmountUtils";
import { useLoggedUser } from "../../../../../utils/useLoggedUser";
import {
  consolidateTrainingExpenseRowsForDisplay,
  isTrainingExpenseDisplayRow,
  resolveTrainingAssignedDate,
  resolveTrainingAssigneeId,
  resolveTrainingEndDate,
  trainingMatchesEmployeeProject,
  trainingMatchesProjectMeta,
} from "../../../../../utils/trainingOtherExpenseSync";
import { persistManagerHardwareOtherExpenseBeforeComplete, markHardwareOtherExpenseGroupComplete } from "../../OperationToManagerAccess/OPProjectOngoingPM/OPHardwareOngoingPM";

const isDescriptionTaskComplete = (entry) =>
  entry?.isTaskComplete === true || entry?.isTaskcomplete === true;

/** Trim description so manager "Airfare" matches operation person "Airfare ". */
const normalizeOtherExpenseDescription = (description) =>
  String(description ?? "").trim();

const descriptionsMatch = (left, right) =>
  normalizeOtherExpenseDescription(left) ===
  normalizeOtherExpenseDescription(right);

/** Self consolidated Other Expenses: manager expenses + all OP expenses on file. */
const getSelfConsolidatedDisplayExpenses = (allEntries, loggedUserId) => {
  const entries = allEntries || [];
  const ownerEntry = entries.find(
    (e) => String(e?.taskownerid || "") === String(loggedUserId || "")
  );
  const managerExpenses = parseAmountNumber(ownerEntry?.expenses);
  const opEntries = entries.filter(
    (e) => String(e?.taskownerid || "") !== String(loggedUserId || "")
  );
  return managerExpenses + sumOpExpensesForSelfManagerInput(opEntries);
};

const getOpEntryStoredExpenses = (entry) => {
  const n = parseAmountNumber(
    entry?.expenses ?? entry?.Expenses ?? entry?.expense
  );
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Editable expenses field: empty when zero so default "0" does not block cursor placement. */
const getSelfExpenseInputDisplay = (consolidatedTotal, draft) => {
  if (draft !== undefined) return draft;
  if (!consolidatedTotal || consolidatedTotal <= 0) return "";
  return formatAmountForInput(consolidatedTotal);
};

/** OP expenses on file for manager input display/save split (does not alter consolidated totals). */
const sumOpExpensesForSelfManagerInput = (opEntries) =>
  (opEntries || []).reduce((sum, e) => sum + (Number(e?.expenses) || 0), 0);

/**
 * Self consolidated save/display split: use OP submitted expenses when present.
 * When OP expenses are 0/null and the line is not complete, use OP Amount (`value`)
 * so manager entering total 40 with OP amount 20 saves manager expenses as 20.
 */
const getOpShareForSelfConsolidatedDeduction = (opEntry) => {
  const storedExpenses = getOpEntryStoredExpenses(opEntry);
  if (storedExpenses != null) return storedExpenses;
  if (isDescriptionTaskComplete(opEntry)) return 0;
  const amountValue = parseAmountNumber(opEntry?.value ?? opEntry?.Value);
  return Number.isFinite(amountValue) && amountValue > 0 ? amountValue : 0;
};

const sumOpShareForSelfConsolidatedDeduction = (opEntries) =>
  (opEntries || []).reduce(
    (sum, e) => sum + getOpShareForSelfConsolidatedDeduction(e),
    0
  );

const SelfTaskTrackManagerHardware = ({
  handlerefresh,
  row,
  employees = [],
  setIsLoading,
  matchedHardwarePartsManager,
  managerSelfPartIdsByAssemblyDescription = {},
  matchedHardwareDescriptionsPartsManager,
  allDescriptionEntriesForProject,
  showListState,
  isViewOnly = false,
}) => {
  const dispatch = useDispatch();
  const { formatDateByCountry, getDatePlaceholder, getDateFormat } =
    DateConversion();
  const currency = useCurrencySymbol();
  const USAComma = CommaConvert();
  const loggedUser = useLoggedUser();
  const { mytrainings } = useSelector((state) => state.myTrainings);
  const { enqueueSnackbar } = useSnackbar();
  const [loadingRows, setLoadingRows] = useState({});
  /** Immediate UI for Other Expenses checkbox until Redux refresh catches up */
  const [descriptionCompleteChecked, setDescriptionCompleteChecked] =
    useState({});

  const [filteredHardwareDescription, setFilteredHardwareDescription] = useState([]);

  useEffect(() => {
    setDescriptionCompleteChecked((prev) => {
      if (!Object.keys(prev).length) return prev;
      const sourceList =
        allDescriptionEntriesForProject?.length > 0
          ? allDescriptionEntriesForProject
          : matchedHardwareDescriptionsPartsManager;
      const next = { ...prev };
      let changed = false;
      filteredHardwareDescription.forEach((entry, idx) => {
        const rowKey = entry._id || `desc-${idx}`;
        if (next[rowKey] === undefined) return;
        const descTrim = normalizeOtherExpenseDescription(entry?.description);
        const entriesInGroup =
          (sourceList || []).filter((e) =>
            descriptionsMatch(e?.description, descTrim)
          ) || entry.allEntriesInGroup || [entry];
        const serverComplete = entriesInGroup.every((e) =>
          isDescriptionTaskComplete(e)
        );
        if (serverComplete === next[rowKey]) {
          delete next[rowKey];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [
    allDescriptionEntriesForProject,
    matchedHardwareDescriptionsPartsManager,
    filteredHardwareDescription,
  ]);

  useEffect(() => {
    // Use full project list when provided (HardwareTrack) so consolidated rows have all entries (manager + OP) for checkbox
    const resolveRefId = (ref) => {
      if (ref == null || ref === "") return "";
      if (typeof ref === "object") return String(ref?._id ?? ref?.id ?? "").trim();
      return String(ref).trim();
    };
    const trainingsList = Array.isArray(mytrainings?.trainings)
      ? mytrainings.trainings
      : Array.isArray(mytrainings)
        ? mytrainings
        : [];

    const rowLeadLike = row?.lead || row?.Lead || {};
    const projectMeta = {
      rowId: row?._id,
      projectIds: [row?._id, row?.ProjectID, row?.projectid, row?.projectId, rowLeadLike?._id].filter(Boolean),
      quotationIds: [
        row?.quotationId,
        row?.quotationID,
        row?.quotationid,
        row?.QuotationId,
        rowLeadLike?.quotationId,
        rowLeadLike?.quotationID,
        rowLeadLike?.quotationid,
      ].filter(Boolean),
      projectNames: [row?.ProjectName, row?.projectname, rowLeadLike?.ProjectName, rowLeadLike?.projectname].filter(Boolean),
      leadIds: [row?.leadid, row?.leadId, row?.LeadId, row?.LeadID, rowLeadLike?.leadid, rowLeadLike?.leadId].filter(Boolean),
    };

    const assignedEmails = new Set(
      (row?.Assignedperson || [])
        .map((person) => String(person?.email || person?.EmpEmail || "").trim().toLowerCase())
        .filter(Boolean)
    );
    const assignedEmployees = (employees || []).filter((emp) =>
      assignedEmails.has(String(emp?.EmpEmail || "").trim().toLowerCase())
    );
    if (loggedUser?._id && !assignedEmployees.some((emp) => String(emp?._id) === String(loggedUser._id))) {
      assignedEmployees.push(loggedUser);
    }

    const isPmEmployee = (emp) =>
      (emp?.customRoles || []).some((r) =>
        String(r || "").toLowerCase().includes("project manager")
      );

    const trainingAssigneeMatches = (training, emp) => {
      const assignToRaw = training?.assignedto ?? training?.assignedTo;
      const assigneeId = String(emp?._id || "").trim();
      const assigneeEmail = String(emp?.EmpEmail || emp?.email || "")
        .trim()
        .toLowerCase();
      const assignToId = resolveTrainingAssigneeId(assignToRaw);
      const assignToEmail =
        typeof assignToRaw === "string" && assignToRaw.includes("@")
          ? assignToRaw.trim().toLowerCase()
          : String(training?.assignedToEmail || training?.assigneeEmail || "")
              .trim()
              .toLowerCase();
      if (assigneeId && assignToId && String(assignToId) === assigneeId) return true;
      if (assigneeEmail && assignToEmail && assigneeEmail === assignToEmail) return true;
      if (
        typeof assignToRaw === "string" &&
        assignToRaw.trim().toLowerCase() === "self" &&
        isPmEmployee(emp) &&
        loggedUser?._id &&
        String(loggedUser._id) === String(emp._id)
      ) {
        return true;
      }
      return false;
    };

    const employeesForTrainingRollup = loggedUser?._id
      ? assignedEmployees.filter((emp) => String(emp._id) === String(loggedUser._id))
      : assignedEmployees;

    const trainingRows = employeesForTrainingRollup.flatMap((emp) => {
      const assigneeId = resolveRefId(emp?._id || emp?.id);
      const assigneeEmail = String(emp?.EmpEmail || emp?.email || "").trim().toLowerCase();
      const assigneeName = [
        emp?.EmpFirstName,
        emp?.EmpLastName,
      ].filter(Boolean).join(" ").trim().toLowerCase() ||
        String(emp?.name || emp?.fullName || "").trim().toLowerCase();
      if (!assigneeId && !assigneeEmail && !assigneeName) return [];

      const seenTrainingIds = new Set();
      const matched = trainingsList.filter((t) => {
        const trainingId = String(t?._id ?? "").trim();
        if (trainingId && seenTrainingIds.has(trainingId)) return false;
        if (!trainingAssigneeMatches(t, emp)) return false;
        const assignToRaw = t?.assignedto ?? t?.assignedTo;
        const assignToIsSelfLabel =
          typeof assignToRaw === "string" &&
          assignToRaw.trim().toLowerCase() === "self";
        const projectOk =
          trainingMatchesEmployeeProject(t, assigneeId, {
            ...projectMeta,
            assigneeEmail,
          }) ||
          (assignToIsSelfLabel && trainingMatchesProjectMeta(t, projectMeta));
        if (!projectOk) return false;
        if (trainingId) seenTrainingIds.add(trainingId);
        return true;
      });

      const normalized = matched.map((t) => {
        const amount = Number(
          String(t?.trainingcost ?? t?.trainingCost ?? 0)
            .toString()
            .replace(/,/g, "")
        );
        const notes = t?.notes ?? t?.note ?? "";
        const startdate = resolveTrainingAssignedDate(t, row?.DateRequested);
        const enddate = resolveTrainingEndDate(t);
        return {
          amount: Number.isFinite(amount) ? amount : 0,
          notes: String(notes || "").trim(),
          startdate,
          enddate,
        };
      });

      const total = normalized.reduce((sum, x) => sum + (Number(x.amount) || 0), 0);
      const joinedNotes = Array.from(new Set(normalized.map((x) => x.notes).filter(Boolean))).join("; ");
      if (total <= 0 && !joinedNotes) return [];

      return [
        {
          _id: `training_cost_${assigneeId || assigneeEmail}`,
          taskownerid: assigneeId || assigneeEmail,
          projectid: row?._id,
          description: "Training Cost",
          value: total,
          notes: joinedNotes,
          startdate: normalized.map((x) => x.startdate).find(Boolean) ?? row?.DateRequested ?? null,
          enddate: normalized.map((x) => x.enddate).find(Boolean) ?? null,
          isTaskComplete: true,
          expenses: total,
          _isTrainingCost: true,
        },
      ];
    });

    const sourceList =
      allDescriptionEntriesForProject?.length > 0
        ? allDescriptionEntriesForProject
        : matchedHardwareDescriptionsPartsManager;
    const loggedUserId = String(loggedUser?._id || "");
    const withoutStaleTrainingForSelf = (sourceList || []).filter((entry) => {
      if (!isTrainingExpenseDisplayRow(entry)) return true;
      if (trainingRows.length > 0) return false;
      if (!loggedUserId) return true;
      const owner = String(entry?.taskownerid ?? "").trim();
      return owner !== loggedUserId;
    });
    const sourceWithTraining =
      trainingRows.length > 0
        ? [...withoutStaleTrainingForSelf, ...trainingRows]
        : sourceList;
    if (!sourceWithTraining?.length) {
      setFilteredHardwareDescription([]);
      return;
    }

    // 1️⃣ Group by trimmed description and sum values
    const sourceWithoutDuplicateTraining =
      consolidateTrainingExpenseRowsForDisplay(sourceWithTraining);

    const groupedEntries = Object.values(
      sourceWithoutDuplicateTraining.reduce((acc, entry) => {
        const desc = normalizeOtherExpenseDescription(entry?.description);
        if (!desc) return acc;

        if (!acc[desc]) {
          acc[desc] = { value: 0, expenses: 0, allEntries: [] };
        }
        acc[desc].value += Number(entry?.value) || 0;
        acc[desc].expenses += Number(entry?.expenses) || 0;
        acc[desc].allEntries.push(entry);
        return acc;
      }, {})
    );

    // 2️⃣ Pick entry with loggedUser._id if exists, else pick any; keep allEntriesInGroup for consolidated checkbox
    const filteredEntries = groupedEntries?.map((group) => {
      const ownerEntry = group?.allEntries?.find(
        (e) => String(e?.taskownerid || "") === loggedUserId
      );
      const pickedEntry = ownerEntry || group?.allEntries[0];
      const displayExpenses = getSelfConsolidatedDisplayExpenses(
        group?.allEntries,
        loggedUser?._id
      );

      return {
        ...pickedEntry,
        value: group?.value,
        expenses: displayExpenses,
        allEntriesInGroup: group?.allEntries || [pickedEntry],
      };
    });

    setFilteredHardwareDescription(filteredEntries);
  }, [matchedHardwareDescriptionsPartsManager, allDescriptionEntriesForProject, loggedUser, mytrainings, row, employees]);

  const handleHardwarePartsMarkComplete = async (entry) => {
    if (isViewOnly) return;
    const projectid = row?._id;
    const descriptionKey = entry?.assemblepartdescription;
    const managerPartIds =
      managerSelfPartIdsByAssemblyDescription?.[descriptionKey] || [];
    const taskIdsToComplete = Array.from(
      new Set([entry?._id, ...managerPartIds].filter(Boolean))
    );
    setLoadingRows((prev) => ({ ...prev, [entry?._id]: true }));
    setIsLoading(true);
    try {
      for (const taskid of taskIdsToComplete) {
        await dispatch(markHardwarePartComplete({ projectid, taskid }));
      }
      dispatch(fetchHardwareProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
      enqueueSnackbar("Part Completed Successfully", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar("Failed To Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      setIsLoading(false);
    }
  };

  const handleMarkNonCompleteHardware = async (entry) => {
    if (isViewOnly) return;
    const projectid = row?._id;
    const descriptionKey = entry?.assemblepartdescription;
    const managerPartIds =
      managerSelfPartIdsByAssemblyDescription?.[descriptionKey] || [];
    const taskIdsToUpdate = Array.from(
      new Set([entry?._id, ...managerPartIds].filter(Boolean))
    );
    setLoadingRows((prev) => ({ ...prev, [entry?._id]: true }));
    setIsLoading(true);
    try {
      for (const taskid of taskIdsToUpdate) {
        await dispatch(markNotHardwarePartComplete({ projectid, taskid }));
      }
      dispatch(fetchHardwareProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
      enqueueSnackbar("Part Not-Completed Successfully", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      setIsLoading(false);
    }
  };

  const [updatedHardwareEntries, setUpdatedHardwareEntries] = useState({});

  const handleDateSelfEntryHardware = useCallback(
    async (entry) => {
      if (isViewOnly) return;
      const matchedEntry = updatedHardwareEntries[entry._id] || {};
      const matchedStartDate = matchedEntry.startdate || null;
      const matchedEndDate = matchedEntry.enddate || null;

      const updatedData = {
        description: entry.assemblepartdescription,
        startdate: matchedStartDate ?? entry.startdate ?? null,
        enddate: matchedEndDate ?? entry.enddate ?? null,
      };

      try {
        await dispatch(
          updateOngoingHardwareParts({
            projectId: row?._id,
            id: entry._id,
            updatedData,
          })
        );
        dispatch(fetchWorkEntriesProjectTracking());
        dispatch(fetchDescriptionTasksProjectTracking());
      } catch {
        enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
      } finally {
        handlerefresh();
        setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
        setIsLoading(false);
      }
    },
    [
      handlerefresh,
      updatedHardwareEntries,
      row?._id,
      dispatch,
      enqueueSnackbar,
      setLoadingRows,
      setIsLoading,
      isViewOnly,
    ]
  );

  const handleDateChange = useCallback(
    (date, field, entry) => {
      if (isViewOnly) return;
      setUpdatedHardwareEntries((prev) => ({
        ...prev,
        [entry._id]: {
          ...prev[entry._id],
          [field]: date,
        },
      }));
      if (field === "enddate") {
        const newentry = {
          ...entry,
          enddate: date,
        };
        handleDateSelfEntryHardware(newentry);
      }
    },
    [handleDateSelfEntryHardware, isViewOnly]
  );

  const [updatedDateDescription, setUpdatedDateDescription] = useState({});

  const handleDescriptionDateSubmit = useCallback(
    async (entry) => {
      if (isViewOnly) return;
      const matchedEntry = updatedDateDescription[entry._id] || {};
      const matchedStartDate = matchedEntry.startdate || null;
      const matchedEndDate = matchedEntry.enddate || null;

      // Use manager's actual value from source list; do not save consolidated sum (display-only)
      const sourceList = allDescriptionEntriesForProject?.length > 0 ? allDescriptionEntriesForProject : matchedHardwareDescriptionsPartsManager;
      const rawEntry = (sourceList || []).find((e) => e._id === entry._id);
      const valueToSave = rawEntry != null ? rawEntry.value : entry.value;

      const updatedData = {
        description: entry.description,
        value: valueToSave,
        notes: entry.notes,
        typeservice: entry.typeservice,
        startdate: matchedStartDate ?? entry.startdate ?? null,
        enddate: matchedEndDate ?? entry.enddate ?? null,
      };
      try {
        await dispatch(
          updateOngoingDescription({
            projectId: row?._id,
            id: entry._id,
            updatedData,
          })
        );
        dispatch(fetchWorkEntriesProjectTracking());
        dispatch(fetchDescriptionTasksProjectTracking());
      } catch {
        enqueueSnackbar("Failed To Add Date", { variant: "error" });
      } finally {
        handlerefresh();
        setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
        setIsLoading(false);
      }
    },
    [
      updatedDateDescription,
      row,
      dispatch,
      enqueueSnackbar,
      handlerefresh,
      setLoadingRows,
      setIsLoading,
      allDescriptionEntriesForProject,
      matchedHardwareDescriptionsPartsManager,
      isViewOnly,
    ]
  );

  const handleDescriptionDateChange = useCallback(
    (date, field, entry) => {
      if (isViewOnly) return;
      setUpdatedDateDescription((prev) => ({
        ...prev,
        [entry._id]: {
          ...prev[entry._id],
          [field]: date,
        },
      }));
      if (field === "enddate") {
        const newentry = {
          ...entry,
          enddate: date,
        };
        handleDescriptionDateSubmit(newentry);
      }
    },
    [handleDescriptionDateSubmit, isViewOnly]
  );

  const [editedDescription, setEditedDescription] = useState({});
  const [editedDescriptionNotes, setEditedDescriptionNotes] = useState({});

  const isOverdueIncomplete = (entry) => {
    const isIncomplete =
      entry?.isTaskComplete === false || entry?.isTaskcomplete === false;
    if (!isIncomplete || !entry?.enddate) return false;
    const endDate = new Date(entry.enddate);
    if (isNaN(endDate.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return endDate < today;
  };

  const parseExpenseInput = (val) => {
    if (val == null || val === "") return null;
    const parsed = parseAmountNumber(val);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getSelfConsolidatedExpenseContext = (entryId, entryFromRow) => {
    const sourceList =
      allDescriptionEntriesForProject?.length > 0
        ? allDescriptionEntriesForProject
        : matchedHardwareDescriptionsPartsManager;
    const loggedUserId = String(loggedUser?._id || "");
    const displayEntry =
      (sourceList || []).find((e) => e._id === entryId) || entryFromRow;
    const descTrim = normalizeOtherExpenseDescription(
      displayEntry?.description ?? entryFromRow?.description
    );
    const entriesInGroup = (sourceList || []).filter((e) =>
      descriptionsMatch(e?.description, descTrim)
    );
    const opEntries = entriesInGroup.filter(
      (e) => String(e?.taskownerid || "") !== loggedUserId
    );
    const rawManagerEntry = entriesInGroup.find(
      (e) => String(e?.taskownerid || "") === loggedUserId
    );
    const opExpensesSum = opEntries.reduce(
      (sum, e) =>
        sum +
        (isDescriptionTaskComplete(e) ? Number(e?.expenses) || 0 : 0),
      0
    );
    const opInputBase = sumOpExpensesForSelfManagerInput(opEntries);
    const opDeductionBase = sumOpShareForSelfConsolidatedDeduction(opEntries);

    return {
      displayEntry,
      managerPersistEntry: rawManagerEntry,
      opEntries,
      entriesInGroup,
      opExpensesSum,
      opInputBase,
      opHasAnyExpenses: opInputBase > 0,
      opDeductionBase,
      opHasDeductionBase: opDeductionBase > 0,
      managerValue: Number(rawManagerEntry?.value) || 0,
      consolidatedValue: Number(entryFromRow?.value) || 0,
      managerStoredExpenses: Number(rawManagerEntry?.expenses) || 0,
    };
  };

  const getConsolidatedExpenseInputDisplay = (entryId, entryFromRow) => {
    if (editedDescription[entryId] !== undefined) {
      return parseExpenseInput(editedDescription[entryId]) ?? 0;
    }
    const {
      opEntries,
      opInputBase,
      opHasAnyExpenses,
      managerStoredExpenses,
    } = getSelfConsolidatedExpenseContext(entryId, entryFromRow);
    // Display only OP expenses already entered — not OP Amount (`value`) when expenses are empty.
    // Save still subtracts OP value via opDeductionBase in persistManagerConsolidatedExpense.
    if (opEntries.length > 0 && opHasAnyExpenses) {
      return opInputBase + managerStoredExpenses;
    }
    return managerStoredExpenses;
  };

  const getSelfRowExpenseTotal = (entry, entriesInGroup) => {
    const groupComplete = (entriesInGroup || []).every((e) =>
      isDescriptionTaskComplete(e)
    );
    if (groupComplete || isDescriptionTaskComplete(entry)) {
      return getSelfConsolidatedDisplayExpenses(
        entriesInGroup,
        loggedUser?._id
      );
    }
    return getConsolidatedExpenseInputDisplay(entry._id, entry);
  };

  const revertDescriptionCheckbox = (rowKey, serverComplete) => {
    setDescriptionCompleteChecked((prev) => ({
      ...prev,
      [rowKey]: serverComplete,
    }));
  };

  const handleOtherExpenseCheckboxClick = async (
    entry,
    entriesInGroup,
    rowLoadingKey,
    serverGroupComplete
  ) => {
    if (isViewOnly || loadingRows[rowLoadingKey]) return;

    const nextChecked = !(
      descriptionCompleteChecked[rowLoadingKey] ?? serverGroupComplete
    );
    setDescriptionCompleteChecked((prev) => ({
      ...prev,
      [rowLoadingKey]: nextChecked,
    }));

    const currentExpenses = getConsolidatedExpenseInputDisplay(
      entry._id,
      entry
    );
    const startDate =
      updatedDateDescription[entry._id]?.startdate || entry.startdate;
    const endDate =
      updatedDateDescription[entry._id]?.enddate || entry.enddate;

    if (nextChecked) {
      if (!startDate || !endDate) {
        revertDescriptionCheckbox(rowLoadingKey, serverGroupComplete);
        enqueueSnackbar("Please Select End Date", { variant: "error" });
        return;
      }
      if (!currentExpenses || currentExpenses <= 0) {
        revertDescriptionCheckbox(rowLoadingKey, serverGroupComplete);
        enqueueSnackbar("Please Enter Expense", { variant: "error" });
        return;
      }

      setLoadingRows((prev) => ({ ...prev, [rowLoadingKey]: true }));
      setIsLoading(true);
      try {
        const expenseSaved = await persistManagerHardwareOtherExpenseBeforeComplete({
          dispatch,
          projectId: row?._id,
          entry,
          entriesInGroup,
          allDescriptionEntriesForProject,
          loggedUserId: loggedUser?._id,
          currentExpenses,
          startDate,
          endDate,
          editedDescription,
          editedDescriptionNotes,
          persistManagerConsolidatedExpense,
        });
        if (!expenseSaved) {
          revertDescriptionCheckbox(rowLoadingKey, serverGroupComplete);
          return;
        }

        setEditedDescription((prev) => {
          const next = { ...prev };
          delete next[entry._id];
          return next;
        });

        const managerEndDate = endDate ?? entry?.enddate ?? null;
        await markHardwareOtherExpenseGroupComplete({
          dispatch,
          projectId: row?._id,
          entriesInGroup,
          managerEndDate,
          managerStartDate: startDate ?? entry?.startdate ?? null,
          complete: true,
          loggedUserId: loggedUser?._id,
          skipPreUpdate: true,
        });
        handlerefresh();
        enqueueSnackbar("Description Completed Successfully", {
          variant: "success",
        });
      } catch {
        revertDescriptionCheckbox(rowLoadingKey, serverGroupComplete);
        enqueueSnackbar("Failed To Complete Task", { variant: "error" });
      } finally {
        setLoadingRows((prev) => ({ ...prev, [rowLoadingKey]: false }));
        setIsLoading(false);
      }
      return;
    }

    setEditedDescription((prev) => {
      const next = { ...prev };
      delete next[entry._id];
      return next;
    });

    setLoadingRows((prev) => ({ ...prev, [rowLoadingKey]: true }));
    setIsLoading(true);
    try {
      await markHardwareOtherExpenseGroupComplete({
        dispatch,
        projectId: row?._id,
        entriesInGroup,
        complete: false,
        loggedUserId: loggedUser?._id,
      });
      handlerefresh();
      enqueueSnackbar("Description Not-Completed Successfully", {
        variant: "success",
      });
    } catch {
      revertDescriptionCheckbox(rowLoadingKey, serverGroupComplete);
      enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [rowLoadingKey]: false }));
      setIsLoading(false);
    }
  };

  /** Same OP expense sync as mark-complete when OP expenses are empty and line is open. */
  const syncIncompleteOpExpensesForConsolidatedGroup = async (opEntries) => {
    const projectid = row?._id;
    if (!projectid) return;
    for (const opEntry of opEntries || []) {
      if (!opEntry?._id || isDescriptionTaskComplete(opEntry)) continue;
      const entryValue = parseAmountNumber(opEntry?.value ?? opEntry?.Value);
      const storedOpExpenses = getOpEntryStoredExpenses(opEntry);
      const expensesToSave =
        storedOpExpenses != null ? storedOpExpenses : entryValue;
      if (!Number.isFinite(expensesToSave) || expensesToSave <= 0) continue;
      await dispatch(
        updateOngoingDescription({
          projectId: projectid,
          id: opEntry._id,
          updatedData: {
            value: entryValue,
            expenses: expensesToSave,
          },
        })
      );
    }
  };

  // Self consolidated row: if OP already has expenses, user enters total and manager = entered − OP.
  // If OP has no expenses yet, blur save also updates each incomplete OP row (same as complete action).
  const persistManagerConsolidatedExpense = async (
    entryId,
    entryFromRow,
    enteredValueRaw
  ) => {
    if (isViewOnly) return false;
    if (!entryId) return false;

    const enteredValue =
      typeof enteredValueRaw === "number"
        ? enteredValueRaw
        : parseExpenseInput(enteredValueRaw);
    if (enteredValue == null || enteredValue <= 0) return false;

    const { managerPersistEntry, opEntries, opDeductionBase, opHasDeductionBase } =
      getSelfConsolidatedExpenseContext(entryId, entryFromRow);
    if (!managerPersistEntry?._id) return false;

    let managerExpensesToSave = enteredValue;
    const useConsolidatedEntry = opEntries.length > 0 && opHasDeductionBase;

    if (useConsolidatedEntry) {
      managerExpensesToSave = enteredValue - opDeductionBase;
      if (managerExpensesToSave < 0) {
        enqueueSnackbar(
          "Total expenses cannot be less than the operation person expenses already submitted",
          { variant: "error" }
        );
        return false;
      }
    }

    try {
      await dispatch(
        updateOngoingDescription({
          projectId: row?._id,
          id: managerPersistEntry._id,
          updatedData: { expenses: managerExpensesToSave },
        })
      );
      if (useConsolidatedEntry) {
        await syncIncompleteOpExpensesForConsolidatedGroup(opEntries);
      }
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
      dispatch(fetchHardwareProjectTracking());
      return true;
    } catch {
      enqueueSnackbar("Failed To Add Expense", { variant: "error" });
      return false;
    }
  };

  const handleDescriptionExpensesUpdate = async (entryId, entryFromRow) => {
    if (editedDescription[entryId] === undefined) return;
    const enteredTotal = parseExpenseInput(editedDescription[entryId]);
    if (enteredTotal == null || enteredTotal <= 0) return;

    setLoadingRows((prev) => ({ ...prev, [entryId]: true }));
    setIsLoading(true);
    try {
      const saved = await persistManagerConsolidatedExpense(
        entryId,
        entryFromRow,
        enteredTotal
      );
      if (saved) {
        handlerefresh();
        setEditedDescription((prev) => {
          const next = { ...prev };
          delete next[entryId];
          return next;
        });
      }
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entryId]: false }));
      setIsLoading(false);
    }
  };

  const handleDescriptionNotesUpdate = async (entryId) => {
    if (isViewOnly) return;
    const value = editedDescriptionNotes[entryId]; // get the value for this specific task
    if (!entryId) return;
    const updatedData = {
      notes: value,
    };
    try {
      await dispatch(
        updateOngoingDescription({
          projectId: row?._id,
          id: entryId,
          updatedData,
        })
      );
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    } catch {
      enqueueSnackbar("Failed To Add Date", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entryId]: false }));
      setIsLoading(false);
    }
  };

  const handleHardwareQuantityUpdate = async (entryId) => {
    if (isViewOnly) return;
    if (!entryId) return;

    const entryData = updatedHardwareEntries[entryId];
    if (!entryData) return;

    const updatedData = {
      quantity: entryData.quantity,
      totalprice: entryData.totalprice,
    };
    try {
      setLoadingRows((prev) => ({ ...prev, [entryId]: true }));

      await dispatch(
        updateOngoingHardwareParts({
          projectId: row?._id,
          id: entryId,
          updatedData,
        })
      );

      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    } catch (error) {
      enqueueSnackbar("Failed to update quantity", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entryId]: false }));
      setIsLoading(false);
    }
  };

  return (
    <div>
      <>
        {showListState === "tasktable" && (
          <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden">
            <div className="z-[50] sticky top-0  flex bg-[#e8e8e8] text-black text-[10px] font-semibold break-all whitespace-normal">
              <div className="p-0.5 break-words whitespace-normal w-[11%] flex justify-center items-center">
                Part Name
              </div>
              <div className="p-1 break-words whitespace-normal w-[10%] flex justify-center items-center">
                Part No
              </div>
              <div className="p-0.5 break-words whitespace-normal w-[10%] flex justify-center items-center">
                {" Quantity"}
              </div>
              <div className="p-0.5 break-words whitespace-normal w-[11%] flex justify-center items-center">
                Total Price ({currency})
              </div>
              <div className="p-0.5 break-words whitespace-normal w-[14%] flex justify-center items-center">
                Start Date
              </div>
              <div className="p-0.5 break-words whitespace-normal w-[14%] flex justify-center items-center">
                End Date
              </div>
              <div className="p-0.5 break-words whitespace-normal w-[24%] flex justify-center items-center">
                Tracker
              </div>
              <div className="p-0.5 break-words whitespace-normal w-[6%] flex justify-center items-center">
                Action
              </div>
            </div>

            <div className="flex text-[10px] break-words whitespace-normal max-h-14">
              <div className="p-0.5 text-center break-words whitespace-normal border-b border-[#dee2e6] w-[11%] flex justify-center items-center">
                {"Total"}
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[10%]"></div>
              <div className="p-0.5 break-all whitespace-normal border-b border-[#dee2e6] w-[10%] flex justify-center items-center">
                {USAComma(
                  matchedHardwarePartsManager?.reduce(
                    (sum, item) => sum + (item?.quantity || 0),
                    0
                  )
                )}{" "}
              </div>
              <div className="p-0.5 break-all whitespace-normal border-b border-[#dee2e6] w-[11%] flex justify-center items-center">
                {USAComma(
                  matchedHardwarePartsManager?.reduce(
                    (sum, item) => sum + (item?.totalprice || 0),
                    0
                  )
                )}
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[14%] text-center"></div>
              <div className="p-1 border-b border-[#dee2e6] w-[14%] text-center"></div>
              <div className="p-2.5 border-b border-[#dee2e6] w-[24%] text-center">
                {(() => {
                  const allEntries = matchedHardwarePartsManager?.length;
                  const completedEntriesCount =
                    matchedHardwarePartsManager?.filter(
                      (entry) => entry.isTaskComplete
                    ).length;
                  const trackerPercentage =
                    allEntries > 0
                      ? (completedEntriesCount / allEntries) * 100
                      : 0;
                  return <ProgressTracker value={trackerPercentage} />;
                })()}
              </div>
              <div className="p-0.5 border-b border-[#dee2e6] w-[6%] text-center"></div>
            </div>

            {matchedHardwarePartsManager?.length > 0 ? (
              matchedHardwarePartsManager?.map((entry, index) => (
                (() => {
                  const showOverdueWarning = isOverdueIncomplete(entry);
                  return (
                <div
                  key={entry}
                  title={showOverdueWarning ? "end date is passed" : ""}
                  className={`flex text-[10px] break-words whitespace-normal max-h-14 cursor-default ${
                    ""
                  }`}
                >
                  <div
                    className="p-0.5 break-words whitespace-normal border-b border-[#dee2e6] w-[11%] max-h-14 overflow-auto flex justify-center items-center"
                    title={`${entry.assemblepartdescription}`}
                  >
                    {entry.assemblepartdescription}
                  </div>
                  <div className="p-0.5 break-all whitespace-normal border-b border-[#dee2e6] w-[10%] flex justify-center items-center">
                    {entry.partno}
                  </div>
                  <div className="p-0.5 break-all whitespace-normal border-b border-[#dee2e6] w-[10%] flex justify-center items-center">
                    {entry.isTaskComplete ? (
                      <span>{entry.quantity || ""}</span>
                    ) : (
                      <input
                        type="text"
                        disabled={isViewOnly}
                        value={
                          updatedHardwareEntries[entry._id]?.quantity ??
                          entry.quantity ??
                          ""
                        }
                        placeholder="enter quantity"
                        className={`border border-gray-200 w-full p-1 rounded-md text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                        onChange={(e) => {
                          const qty = Number(e.target.value) || 0;
                          setUpdatedHardwareEntries((prev) => ({
                            ...prev,
                            [entry._id]: {
                              ...prev[entry._id],
                              quantity: qty,
                              totalprice: qty * entry.unitprice,
                            },
                          }));
                        }}
                        onBlur={() => handleHardwareQuantityUpdate(entry._id)}
                      />
                    )}
                  </div>
                  <div className="p-0.5 border-b break-all whitespace-normal border-[#dee2e6] w-[11%] flex justify-center items-center">
                    {USAComma(
                      updatedHardwareEntries[entry._id]?.totalprice ??
                      entry.totalprice ??
                      0
                    )}
                  </div>
                  <div className="p-0.5 border-b break-words whitespace-normal border-[#dee2e6] w-[14%] flex justify-center items-center">
                    {entry.isTaskComplete ? (
                      <div className="text-black">
                        {entry.startdate
                          ? formatDateByCountry(entry.startdate)
                          : ""}
                      </div>
                    ) : (
                      <DatePicker
                        dateFormat={getDateFormat()}
                        disabled={isViewOnly}
                        className={`w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                        placeholderText={getDatePlaceholder()}
                        popperClassName="z-[1500]"
                        minDate={
                          entry.startdate
                            ? new Date(entry.startdate)
                            : row?.DateRequested
                              ? new Date(row.DateRequested)
                              : null
                        }
                        maxDate={new Date()}
                        selected={
                          updatedHardwareEntries[entry._id]?.startdate
                            ? updatedHardwareEntries[entry._id].startdate
                            : entry.startdate
                              ? new Date(entry.startdate)
                              : null
                        }
                        onChange={(date) =>
                          handleDateChange(date, "startdate", entry)
                        }
                      />
                    )}
                  </div>
                  <div className="p-0.5 border-b break-words whitespace-normal border-[#dee2e6] w-[14%] flex justify-center items-center">
                    {entry.isTaskComplete ? (
                      <div className="text-black">
                        {entry.enddate
                          ? formatDateByCountry(entry.enddate)
                          : ""}
                      </div>
                    ) : (
                      <DatePicker
                        dateFormat={getDateFormat()}
                        disabled={isViewOnly}
                        className={`w-full h-full text-center outline-none border p-1 ${
                          showOverdueWarning
                            ? "border-orange-400"
                            : "border-gray-300"
                        } rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                        placeholderText={getDatePlaceholder()}
                        popperClassName="z-[1500]"
                        minDate={
                          updatedHardwareEntries[entry._id]?.startdate
                            ? new Date(
                              updatedHardwareEntries[entry._id].startdate
                            )
                            : entry.startdate
                              ? new Date(entry.startdate)
                              : null
                        }
                        selected={
                          updatedHardwareEntries[entry._id]?.enddate
                            ? updatedHardwareEntries[entry._id].enddate
                            : entry.enddate
                              ? new Date(entry.enddate)
                              : null
                        }
                        onChange={(date) =>
                          handleDateChange(date, "enddate", entry)
                        }
                      />
                    )}
                  </div>
                  <div className="p-2.5 border-b border-[#dee2e6] w-[24%] text-center">
                    <ProgressTracker value={entry.isTaskComplete ? 100 : 0} />
                  </div>
                  <div
                    className={` p-0.5 border-b border-[#dee2e6] w-[6%] flex justify-center items-center 
                    ${
                      row.Assignedperson.every(
                        (person) => person.isProjectComplete === true
                      )
                        ? "cursor-default"
                        : ""
                    }`}
                  >
                    {loadingRows[entry._id] ? (
                      <LoadingSpinner />
                    ) : (
                      <Checkbox
                        checked={!!entry.isTaskComplete}
                        disabled={
                          isViewOnly ||
                          !!loadingRows[entry._id] ||
                          row.Assignedperson?.every(
                            (person) => person.isProjectComplete === true
                          )
                        }
                        sx={
                          (() => {
                            const isProjectSubmitted = row.Assignedperson?.every(
                              (person) => person.isProjectComplete === true
                            );
                            if (isViewOnly || isProjectSubmitted) {
                              return {
                                color: "grey.500 !important",
                                "&.Mui-checked": { 
                                  color: "grey.600 !important",
                                },
                                "&.Mui-disabled": {
                                  color: "grey.500 !important",
                                },
                                "&.Mui-checked.Mui-disabled": {
                                  color: "grey.600 !important",
                                }
                              };
                            }
                            return undefined;
                          })()
                        }
                        onChange={async (e) => {
                          if (isViewOnly || loadingRows[entry._id]) return;
                          const isChecked = e.target.checked;
                          const startDate =
                            updatedHardwareEntries[entry._id]?.startdate ||
                            entry.startdate;
                          const endDate =
                            updatedHardwareEntries[entry._id]?.enddate ||
                            entry.enddate;
                          if (isChecked) {
                            if (!startDate || !endDate) {
                              enqueueSnackbar("Please Select End Date", {
                                variant: "error",
                              });
                              return;
                            }
                            await handleHardwarePartsMarkComplete(entry);
                            await handleDateSelfEntryHardware(entry);
                          } else {
                            await handleMarkNonCompleteHardware(entry);
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              );
                })()
              ))
            ) : (
              <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
                <p className="text-red-500 text-center">
                  {" "}
                  No Tracking Task Entries Found
                </p>
              </div>
            )}
          </div>
        )}
        {showListState === "otherexpenses" && (
          <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden">
            <div className="z-[50] flex sticky top-0 bg-[#e8e8e8] text-black text-[10px] font-semibold break-words whitespace-normal">
              <div className="p-0.5 border w-[10%] flex justify-center items-center">
                Description
              </div>
              <div className="p-0.5 border w-[11%] flex justify-center items-center">
                Amount ({currency})
              </div>
              <div className="p-0.5 border w-[14%] flex justify-center items-center">
                Start Date
              </div>
              <div className="p-0.5 border w-[14%] flex justify-center items-center">
                End Date
              </div>
                            <div className="p-0.5 border w-[11%] flex justify-center items-center">
                Expenses ({currency})
              </div>
              <div className="p-0.5 border w-[10%] flex justify-center items-center">
                Notes
              </div>
              <div className="p-1 border w-[24%] flex justify-center items-center">
                Tracker
              </div>
              <div className="p-0.5 border w-[6%] flex justify-center items-center">
                Action
              </div>
            </div>

            <div className="flex text-[10px] break-all whitespace-normal max-h-14">
              <div className="p-0.5 text-center border-b border-[#dee2e6] w-[10%] flex justify-center items-center">
                {"Total"}
              </div>
              <div className="p-0.5 break-all whitespace-normal border-b border-[#dee2e6] w-[11%] flex justify-center items-center">
                {formatAmountComma(
                  filteredHardwareDescription.reduce(
                    (sum, item) => sum + parseAmountNumber(item?.value),
                    0
                  )
                )}
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[14%] flex justify-center items-center"></div>
              <div className="p-1 border-b border-[#dee2e6] w-[14%] flex justify-center items-center"></div>
              <div className="p-0.5 break-all whitespace-normal border-b border-[#dee2e6] w-[11%] flex justify-center items-center">
                {(() => {
                  const expenseTotal = filteredHardwareDescription.reduce(
                    (sum, grp) => {
                      const fullSourceList =
                        allDescriptionEntriesForProject?.length > 0
                          ? allDescriptionEntriesForProject
                          : matchedHardwareDescriptionsPartsManager;
                      const descTrim = normalizeOtherExpenseDescription(
                        grp?.description
                      );
                      const entriesInGroup =
                        fullSourceList?.filter((e) =>
                          descriptionsMatch(e?.description, descTrim)
                        ) || grp.allEntriesInGroup || [grp];
                      return (
                        sum +
                        parseAmountNumber(
                          getSelfRowExpenseTotal(grp, entriesInGroup)
                        )
                      );
                    },
                    0
                  );
                  return expenseTotal > 0 ? formatAmountComma(expenseTotal) : "";
                })()}
              </div>
                            <div className="p-1 border-b border-[#dee2e6] w-[10%] flex justify-center items-center"></div>
              <div className="p-2.5 border-b border-[#dee2e6] w-[24%] text-center">
                {filteredHardwareDescription.length > 0 &&
                filteredHardwareDescription.every((grp) =>
                  (grp.allEntriesInGroup || [grp]).every((e) =>
                    isDescriptionTaskComplete(e)
                  )
                ) ? (
                  <>
                    <ProgressTracker value={100} />
                  </>
                ) : (
                  <>
                    {(() => {
                      const previousValue =
                        filteredHardwareDescription.reduce(
                          (sum, item) => sum + (item?.value || 0),
                          0
                        );
                      const addedExpenses =
                        filteredHardwareDescription.reduce(
                          (sum, grp) => sum + Number(grp?.expenses || 0),
                          0
                        );
                      const trackerPercentage =
                        previousValue > 0
                          ? (addedExpenses / previousValue) * 100
                          : 0;
                      return <ProgressTracker value={trackerPercentage} />;
                    })()}
                  </>
                )}
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[6%] text-center"></div>
            </div>

            {filteredHardwareDescription.length > 0 ? (
              filteredHardwareDescription.map((entry, index) => {
                // Consolidated: ensure we have ALL entries with same description (manager + operation) so checkbox marks both complete
                const fullSourceList =
                  allDescriptionEntriesForProject?.length > 0
                    ? allDescriptionEntriesForProject
                    : matchedHardwareDescriptionsPartsManager;
                const descTrim = normalizeOtherExpenseDescription(
                  entry?.description
                );
                const entriesInGroup =
                  fullSourceList?.filter((e) =>
                    descriptionsMatch(e?.description, descTrim)
                  ) || entry.allEntriesInGroup || [entry];
                const isGroupComplete = entriesInGroup.every((e) =>
                  isDescriptionTaskComplete(e)
                );
                const rowLoadingKey = entry._id || `desc-${index}`;
                const checkboxChecked =
                  descriptionCompleteChecked[rowLoadingKey] ?? isGroupComplete;
                const isProjectSubmitted = row.Assignedperson?.every(
                  (person) => person.isProjectComplete === true
                );
                const showOverdueWarning = isOverdueIncomplete(entry);
                return (
                <div
                  key={entry._id || index}
                  title={showOverdueWarning ? "end date is passed" : ""}
                  className={`flex text-[10px] break-all whitespace-normal max-h-14 cursor-default ${
                    isProjectSubmitted ? "bg-gray-100 text-gray-500" : ""
                  } ${
                      ""
                  }`}
                >
                  <div
                    className="p-0.5 border-b break-words whitespace-normal border-[#dee2e6] w-[10%] max-h-14 overflow-auto flex justify-center items-center"
                    title={`${entry.description}`}
                  >
                    {entry.description}
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[11%] flex justify-center items-center">
                    {formatAmountComma(entry.value)}
                  </div>
                  <div className="p-0.5 border-b border-[#dee2e6] w-[14%] flex justify-center items-center">
                    {entry.isTaskComplete ? (
                      <div className="text-black">
                        {entry.startdate
                          ? formatDateByCountry(entry.startdate)
                          : ""}
                      </div>
                    ) : (
                      <DatePicker
                        dateFormat={getDateFormat()}
                        disabled={isViewOnly}
                        className={`w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                        placeholderText={getDatePlaceholder()}
                        popperClassName="z-[1500]"
                        minDate={
                          entry.startdate
                            ? new Date(entry.startdate)
                            : row?.DateRequested
                              ? new Date(row.DateRequested)
                              : null
                        }
                        maxDate={new Date()}
                        selected={
                          updatedDateDescription[entry._id]?.startdate
                            ? updatedDateDescription[entry._id].startdate
                            : entry.startdate
                              ? new Date(entry.startdate)
                              : null
                        }
                        onChange={(date) =>
                          handleDescriptionDateChange(
                            date,
                            "startdate",
                            entry
                          )
                        }
                      />
                    )}
                  </div>
                  <div className="p-0.5 border-b border-[#dee2e6] w-[14%] flex justify-center items-center">
                    {entry.isTaskComplete ? (
                      <div className="text-black">
                        {entry.enddate
                          ? formatDateByCountry(entry.enddate)
                          : ""}
                      </div>
                    ) : (
                      <DatePicker
                        dateFormat={getDateFormat()}
                        disabled={isViewOnly}
                      className={`w-full h-full text-center outline-none border p-1 ${
                        showOverdueWarning
                          ? "border-orange-400"
                          : "border-gray-300"
                      } rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                        placeholderText={getDatePlaceholder()}
                        popperClassName="z-[1500]"
                        minDate={
                          updatedDateDescription[entry._id]?.startdate
                            ? new Date(
                              updatedDateDescription[entry._id].startdate
                            )
                            : entry.startdate
                              ? new Date(entry.startdate)
                              : null
                        }
                        selected={
                          updatedDateDescription[entry._id]?.enddate
                            ? updatedDateDescription[entry._id].enddate
                            : entry.enddate
                              ? new Date(entry.enddate)
                              : null
                        }
                        onChange={(date) =>
                          handleDescriptionDateChange(date, "enddate", entry)
                        }
                      />
                    )}
                  </div>

                  <div className="p-1 border-b border-[#dee2e6] w-[11%] flex justify-center items-center">
                    {entry.isTaskComplete ? (
                      <span>
                        {formatAmountComma(
                          getSelfConsolidatedDisplayExpenses(
                            entriesInGroup,
                            loggedUser?._id
                          )
                        ) || ""}
                      </span>
                    ) : (
                      <input
                        type="text"
                        disabled={isViewOnly}
                        value={getSelfExpenseInputDisplay(
                          getConsolidatedExpenseInputDisplay(
                            entry._id,
                            entry
                          ),
                          editedDescription[entry._id]
                        )}
                        placeholder="enter expenses"
                        className={`border border-gray-200 w-full rounded-md max-w-full p-1 text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                        onChange={(e) => {
                          if (isViewOnly) return;
                          const value = validateAmountValue(e.target.value);
                          const {
                            opEntries,
                            opHasDeductionBase,
                            managerValue,
                            consolidatedValue,
                          } = getSelfConsolidatedExpenseContext(
                            entry._id,
                            entry
                          );
                          const maxAllowed =
                            opEntries.length > 0 && opHasDeductionBase
                              ? consolidatedValue
                              : managerValue;
                          const numValue = parseAmountNumber(value);
                          if (
                            value !== "" &&
                            Number.isFinite(numValue) &&
                            numValue > maxAllowed
                          ) {
                            enqueueSnackbar(
                              "Expenses is exceeding the Amount",
                              {
                                variant: "error",
                              }
                            );
                          }
                          if (numValue < 0) return;
                          setEditedDescription((prev) => ({
                            ...prev,
                            [entry._id]: value,
                          }));
                        }}
                        onBlur={() =>
                          handleDescriptionExpensesUpdate(entry._id, entry)
                        }
                      />
                    )}
                  </div>

                                    <div
                    title={`${entry.notes}`}
                    className="p-0.5 border-b border-[#dee2e6] w-[10%] flex justify-center items-center break-words whitespace-normal max-h-14 overflow-auto"
                  >
                    {/* {entry.notes || ""} */}
                    {entry.isTaskComplete ? (
                      <span>{entry.notes || ""}</span>
                    ) : (
                      <input
                        type="text"
                        disabled={isViewOnly}
                        value={
                          editedDescriptionNotes[entry._id] ??
                          entry.notes ??
                          ""
                        }
                        placeholder="enter note"
                        className={`border border-gray-200 w-full max-w-full p-1 rounded-md text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                        onChange={(e) => {
                          const value = e.target.value;
                          setEditedDescriptionNotes((prev) => ({
                            ...prev,
                            [entry._id]: value,
                          }));
                        }}
                        onBlur={() => handleDescriptionNotesUpdate(entry._id)}
                      />
                    )}
                  </div>

                  <div className="p-2.5 border-b border-[#dee2e6] w-[24%] text-center">
                    {isGroupComplete ? (
                      <ProgressTracker
                        value={(() => {
                          const rawValue =
                            ((getConsolidatedExpenseInputDisplay(
                              entry._id,
                              entry
                            ) || 0) /
                              entry.value) *
                            100;
                          return rawValue < 100 ? 100 : rawValue;
                        })()}
                      />
                    ) : (
                      <ProgressTracker
                        value={
                          ((getConsolidatedExpenseInputDisplay(
                            entry._id,
                            entry
                          ) || 0) /
                            entry.value) *
                          100
                        }
                      />
                    )}
                  </div>
                  <div className="p-0.5 border-b border-[#dee2e6] w-[6%] flex justify-center items-center">
                    {loadingRows[rowLoadingKey] ? (
                      <LoadingSpinner />
                    ) : (
                      <Checkbox
                        disabled={
                          isViewOnly ||
                          !!loadingRows[rowLoadingKey] ||
                          row.Assignedperson?.every(
                            (person) => person.isProjectComplete === true
                          ) ||
                          ((getConsolidatedExpenseInputDisplay(
                            entry._id,
                            entry
                          ) || 0) /
                            parseAmountNumber(entry.value)) *
                          100 <
                          1
                        }
                        checked={checkboxChecked}
                        sx={
                          isViewOnly || row.Assignedperson?.every(
                            (person) => person.isProjectComplete === true
                          )
                            ? {
                                color: "grey.500",
                                "&.Mui-checked": { color: "grey.600" },
                              }
                            : undefined
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          void handleOtherExpenseCheckboxClick(
                            entry,
                            entriesInGroup,
                            rowLoadingKey,
                            isGroupComplete
                          );
                        }}
                      />
                    )}
                  </div>
                </div>
              );
              })
            ) : (
              <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
                <p className="text-red-500 text-center">
                  {" "}
                  No Tracking Task Entries Found
                </p>
              </div>
            )}
          </div>
        )}
      </>
    </div>
  );
};

export default SelfTaskTrackManagerHardware;
